// autopilot-doctor-ops-checks.ts
//
// Ops checks for `autopilot doctor` (phase-08 Agent H): pack-pin drift, free
// disk, and retention stats. Split from autopilot-doctor.ts to stay ≤200
// lines and to keep P5's existing checks untouched — doctor.ts wires this in
// via ONE call, per phase-08 brief ("nối vào autopilot-doctor.ts, thêm 1
// dòng gọi").
import { join } from "node:path";
import type { AutopilotCheckResult } from "./autopilot-doctor.js";
import { checkReleaseRemoteSupportedInV1 } from "./autopilot-doctor-release-remote-check.js";
import { checkMinFreeDisk, pruneAttemptLogs } from "./log-retention-manager.js";
import {
  captureVerifiedTuple,
  detectPinDrift,
  readVerifiedTuple,
} from "./pack-pin-drift-detector.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";

export interface RunOpsDoctorChecksInput {
  missionWorkspace: string;
  controlRoot: string;
  config: Pick<AutopilotConfig, "logs" | "min_free_disk_mb" | "release">;
  probeClaudeVersion: () => Promise<string>;
  now?: () => Date;
  /**
   * Attempts not yet at a terminal outcome (no closed receipt) — retention
   * never prunes them. `undefined` => caller couldn't determine open-attempt
   * state => `pruneAttemptLogs` fails CLOSED (prunes nothing) rather than
   * defaulting to "all closed" (code-reviewer-260909-p8-m4.md H2).
   */
  openAttemptIds?: readonly string[];
}

export interface RunOpsDoctorChecksResult {
  checks: AutopilotCheckResult[];
  blocksRun: boolean;
}

/** Pack pin / claude binary / settings drift — blocks `run` until `doctor --reverify`. */
async function checkPackPinDrift(input: RunOpsDoctorChecksInput): Promise<AutopilotCheckResult> {
  const current = await captureVerifiedTuple({
    missionWorkspace: input.missionWorkspace,
    controlRoot: input.controlRoot,
    probeClaudeVersion: input.probeClaudeVersion,
    now: input.now,
  });
  const verified = await readVerifiedTuple(input.controlRoot);
  const decision = detectPinDrift(current, verified);
  if (decision.drift) {
    return {
      name: "Pack pin / version drift",
      status: "fail",
      detail: `Lệch tuple đã kiểm ở: ${decision.changedFields.join(", ")} — chạy 'avatar autopilot doctor --reverify' để xác nhận rồi mới 'run' tiếp.`,
    };
  }
  return {
    name: "Pack pin / version drift",
    status: "ok",
    detail: verified
      ? "Khớp tuple đã kiểm gần nhất."
      : "Chưa có tuple đã kiểm — lần capture này sẽ là baseline.",
  };
}

/** `min_free_disk_mb` — blocks new attempt dispatch when below threshold. */
async function checkFreeDisk(input: RunOpsDoctorChecksInput): Promise<AutopilotCheckResult> {
  const decision = await checkMinFreeDisk({
    config: { min_free_disk_mb: input.config.min_free_disk_mb },
    path: input.controlRoot,
  });
  return {
    name: "Free disk",
    status: decision.blocked ? "fail" : "ok",
    detail:
      decision.reason ??
      `Còn ${decision.freeMb.toFixed(0)}MB, trên ngưỡng min_free_disk_mb=${input.config.min_free_disk_mb}MB.`,
  };
}

/** Retention stats — reports what WOULD be pruned without side effects (dry preview via a real prune run against a scratch copy is out of scope for v1; doctor reports current usage only). */
async function checkRetentionStats(input: RunOpsDoctorChecksInput): Promise<AutopilotCheckResult> {
  const now = input.now ?? (() => new Date());
  const result = await pruneAttemptLogs({
    controlRoot: input.controlRoot,
    config: { logs: input.config.logs },
    now: now(),
    // No `?? []` fallback — pruneAttemptLogs itself fails closed on
    // undefined (H2). Defaulting here would silently defeat that.
    openAttemptIds: input.openAttemptIds,
  });
  return {
    name: "Log retention",
    status: "ok",
    detail:
      result.prunedPaths.length > 0
        ? `Đã prune ${result.prunedPaths.length} file (${(result.bytesFreed / (1024 * 1024)).toFixed(1)}MB) quá hạn retention_days=${input.config.logs.retention_days} hoặc vượt max_total_mb=${input.config.logs.max_total_mb}.`
        : `Không có file nào vượt retention_days=${input.config.logs.retention_days} / max_total_mb=${input.config.logs.max_total_mb}.`,
  };
}

/**
 * Runs the ops checks. `blocksRun` mirrors autopilot-doctor.ts's own
 * contract — true when ANY check is `fail` (pin drift, below-threshold free
 * disk, a GitHub release remote; retention pruning itself never blocks).
 */
export async function runOpsDoctorChecks(
  input: RunOpsDoctorChecksInput,
): Promise<RunOpsDoctorChecksResult> {
  const checks = [
    await checkPackPinDrift(input),
    await checkFreeDisk(input),
    await checkRetentionStats(input),
    // A6: v1 releases only through GitLab — fail before any dispatch/push.
    await checkReleaseRemoteSupportedInV1({
      missionWorkspace: input.missionWorkspace,
      release: input.config.release,
    }),
  ];
  return { checks, blocksRun: checks.some((c) => c.status === "fail") };
}

/** Path convention re-exported for autopilot.ts's `doctor --reverify` handler. */
export const OPS_MANIFEST_RELATIVE_PATH = join(".claude", "pack", ".avatar-pack-manifest.json");
